// The townsfolk of Normalton (DESIGN §3): walk between Points of Interest on the nav grid; browse a stall 3–7 s, sit on a
// bench 6–14 s, chat in pairs 5–10 s, tie a shoelace (anywhere now and then, more often at a drain), look around; ride
// the bus away and back; obey the Crier's bell with a per-body stagger; run from a pop; dance behind the parade float.
//
// One brain per NPC body. The brain writes the body's velocity, facing and anim; sim.ts integrates and collides.
import * as T from "./tuning";
import type { Call } from "./tuning";
import { range, pick, weighted } from "./rng";
import * as M from "./map";
import { CALL_ANIM, type Body, type Sim, type Anim } from "./sim";

export type Act = "browse" | "sit" | "chat" | "look" | "rim" | "drain" | "shoelace" | "bus";
/** walk: following a path to `goal`, then `then` · act: doing `act` until `until` · wait: at a chat spot for the
 *  partner · conga: dancing behind the float · board: at the bus door, about to climb in */
export type Task = "idle" | "walk" | "act" | "wait" | "conga" | "board";

export interface NpcBrain {
  task: Task; act: Act; then: Act | "board";
  poi: number;                 // reserved POI (-1 none)
  goal: M.V2; path: M.V2[]; pi: number;
  until: number; lookT: number; waitUntil: number;
  partner: Body | null;        // chat partner
  callAt: number; callKind: Call | null;   // a pending reaction to the bell
  stuckAt: number; stuckX: number; stuckZ: number; replans: number;
  slot: number;                // conga slot
  leaving: boolean;            // chosen to leave on the next bus
  fleeX: number; fleeZ: number;
  face: number;
}
export const newBrain = (): NpcBrain => ({
  task: "idle", act: "look", then: "look", poi: -1, goal: { x: 0, z: 0 }, path: [], pi: 0, until: 0, lookT: 0, waitUntil: 0,
  partner: null, callAt: 0, callKind: null, stuckAt: 0, stuckX: 0, stuckZ: 0, replans: 0, slot: -1, leaving: false, fleeX: 0, fleeZ: 0, face: 0,
});

const ACT_ANIM: Record<Act, Anim> = { browse: "browse", sit: "sit_bench", chat: "chat", look: "idle", rim: "idle", drain: "idle", shoelace: "shoelace", bus: "idle" };

// ---------------------------------------------------------------------------------------------------------------------

/** let go of whatever the body holds: its POI, its bench (stand up in front of it), its chat partner */
export function npcRelease(sim: Sim, b: Body) {
  const br = b.brain; if (!br) return;
  if (br.poi >= 0) {
    const p = M.POIS[br.poi];
    if (b.anchored && p.approach) { b.x = p.approach.x; b.z = p.approach.z; }
    if (sim.poiOwner[br.poi] === b.uid) sim.poiOwner[br.poi] = -1;
    br.poi = -1;
  }
  b.anchored = false;
  if (br.partner) { const q = br.partner; if (q.brain && q.brain.partner === b) q.brain.partner = null; br.partner = null; }
  if (br.task !== "conga") br.task = "idle";
  br.path = []; br.pi = 0;
}

/** choose what to do next (DESIGN §3 routine weights) */
export function npcPlan(sim: Sim, b: Body, allowLace = true) {
  const br = b.brain!; const r = sim.rng, now = sim.time;
  npcRelease(sim, b);
  br.replans = 0;
  if (sim.phase === "results") { startAct(sim, b, "look", Infinity); return; }
  if (br.leaving) {
    const spots = M.poisOf("bus");
    const p = pick(r, spots);
    walkTo(sim, b, p.x + range(r, -0.3, 0.3), p.z + range(r, -0.2, 0.2), "bus", -1);
    return;
  }
  if (allowLace && r() < T.SHOELACE_P) { startAct(sim, b, "shoelace", now + T.SHOELACE_SEC); return; }
  const W = T.ROUTINE_W;
  const kinds: Act[] = ["browse", "sit", "chat", "look", "rim", "drain"];
  const k = kinds[weighted(r, [W.browse, W.sit, W.chat, W.look, W.rim, W.drain])];
  if (k === "chat" && recruitChat(sim, b)) return;
  const kind = k === "browse" ? "browse" : k === "sit" ? "seat" : k === "rim" ? "rim" : k === "drain" ? "drain" : "plaza";
  if (kind === "plaza") { const p = pick(r, M.poisOf("plaza")); walkTo(sim, b, p.x + range(r, -0.8, 0.8), p.z + range(r, -0.8, 0.8), "look", -1); return; }
  const free = M.poisOf(kind).filter((p) => sim.poiOwner[p.id] < 0);
  if (!free.length) { const p = pick(r, M.poisOf("plaza")); walkTo(sim, b, p.x, p.z, "look", -1); return; }
  const p = pick(r, free);
  sim.poiOwner[p.id] = b.uid;
  const at = p.approach || p;
  walkTo(sim, b, at.x, at.z, k === "sit" ? "sit" : (k as Act), p.id);
}

function recruitChat(sim: Sim, b: Body): boolean {
  let best: Body | null = null, bd: number = T.CHAT_RECRUIT_R;
  for (const q of sim.bodies) {
    const qb = q.brain;
    if (q === b || !qb || q.kind !== "npc" || !q.visible || q.mode !== "free" || q.anchored) continue;
    if (qb.leaving || qb.partner || qb.task === "conga" || qb.task === "board" || qb.task === "wait" || (qb.task === "act" && qb.act !== "look" && qb.act !== "rim")) continue;
    const d = Math.hypot(q.x - b.x, q.z - b.z); if (d < bd) { bd = d; best = q; }
  }
  if (!best) return false;
  const mx = (b.x + best.x) / 2, mz = (b.z + best.z) / 2;
  let spot: M.Poi | null = null, sd = Infinity;
  for (const p of M.poisOf("plaza")) { if (sim.poiOwner[p.id] >= 0) continue; const d = Math.hypot(p.x - mx, p.z - mz); if (d < sd) { sd = d; spot = p; } }
  if (!spot) return false;
  sim.poiOwner[spot.id] = b.uid;
  const a = sim.rng() * Math.PI, ox = Math.cos(a) * 0.55, oz = Math.sin(a) * 0.55;
  const A = M.nearestFree(spot.x - ox, spot.z - oz), B = M.nearestFree(spot.x + ox, spot.z + oz);
  npcRelease(sim, best);
  walkTo(sim, b, A.x, A.z, "chat", spot.id);
  walkTo(sim, best, B.x, B.z, "chat", -1);
  b.brain!.partner = best; best.brain!.partner = b;
  b.brain!.face = M.headingTo(A.x, A.z, B.x, B.z); best.brain!.face = M.headingTo(B.x, B.z, A.x, A.z);
  return true;
}

function walkTo(sim: Sim, b: Body, x: number, z: number, then: Act | "board", poi: number) {
  const br = b.brain!;
  br.task = "walk"; br.then = then; br.goal = { x, z }; br.poi = poi >= 0 ? poi : br.poi;
  br.path = M.findPath(b.x, b.z, x, z) || [];
  br.pi = 0; br.stuckAt = sim.time; br.stuckX = b.x; br.stuckZ = b.z;
  if (poi >= 0) br.face = M.POIS[poi].face;
  sim.setAnim(b, "walk");
}

function startAct(sim: Sim, b: Body, act: Act, until: number) {
  const br = b.brain!;
  br.task = "act"; br.act = act; br.until = until; br.lookT = sim.time + range(sim.rng, 1.2, 3);
  b.vx = b.vz = 0;
  sim.setAnim(b, ACT_ANIM[act]);
}

/** arrived at the end of a walk */
function arrive(sim: Sim, b: Body) {
  const br = b.brain!, r = sim.rng, now = sim.time;
  b.vx = b.vz = 0;
  switch (br.then) {
    case "browse": b.face = br.face; startAct(sim, b, "browse", now + range(r, T.BROWSE_MIN, T.BROWSE_MAX)); break;
    case "sit": {
      const p = M.POIS[br.poi];
      if (!p) { npcPlan(sim, b); return; }
      b.x = p.x; b.z = p.z; b.h = p.face; b.face = p.face; b.anchored = true;
      startAct(sim, b, "sit", now + range(r, T.SIT_MIN, T.SIT_MAX)); break;
    }
    case "rim": b.face = br.face; startAct(sim, b, "rim", now + range(r, T.LOOK_MIN, T.LOOK_MAX)); break;
    case "drain":
      b.face = br.face;
      if (r() < T.SHOELACE_DRAIN_P) startAct(sim, b, "shoelace", now + T.SHOELACE_SEC); else startAct(sim, b, "drain", now + range(r, T.LOOK_MIN, T.LOOK_MAX));
      break;
    case "chat": br.task = "wait"; br.waitUntil = now + 12; b.face = br.face; sim.setAnim(b, "idle"); break;
    case "bus": b.face = 0; startAct(sim, b, "bus", Infinity); break;
    case "board": br.task = "board"; sim.setAnim(b, "idle"); break;
    default: b.face = null; startAct(sim, b, "look", now + range(r, T.LOOK_MIN, T.LOOK_MAX));
  }
}

/** a call just finished: pick the routine back up where it was (an act's clock does not run during the call) */
export function npcResumeAfterCall(sim: Sim, b: Body) {
  const br = b.brain!;
  if (br.task === "walk" || br.task === "conga") sim.setAnim(b, "walk");
  else if (br.task === "act") sim.setAnim(b, ACT_ANIM[br.act]);
  else if (br.task === "idle") npcPlan(sim, b, false);
  else sim.setAnim(b, "idle");
}

// ---------------------------------------------------------------------------------------------------------------------

export function npcThink(sim: Sim, b: Body, now: number) {
  const br = b.brain!;
  if (!b.visible || (b.mode !== "free" && b.mode !== "panic")) return;
  if (sim.phase === "results") {
    b.vx = b.vz = 0;
    if (!b.anchored && b.anim !== "idle" && b.anim !== "look_around") sim.setAnim(b, "idle");
    return;
  }
  // the bell: react after this body's own stagger
  if (br.callAt > 0 && now >= br.callAt && br.callKind) {
    const kind = br.callKind; br.callAt = 0; br.callKind = null;
    if (b.mode === "panic") { b.mode = "free"; npcRelease(sim, b); }
    if (b.anchored && kind === "sit") { if (br.task === "act") br.until += T.CALL_HOLD; return; }   // already sitting
    if (b.anchored) { npcRelease(sim, b); br.task = "idle"; }                                        // stand up to do it
    if (br.task === "act") br.until += T.CALL_HOLD;
    if (br.task === "wait") br.waitUntil += T.CALL_HOLD;
    sim.enterCall(b, CALL_ANIM[kind], now + T.CALL_HOLD, false);
    return;
  }
  if (b.mode === "panic") { flee(sim, b); return; }
  switch (br.task) {
    case "idle": npcPlan(sim, b); return;
    case "walk": follow(sim, b, now); return;
    case "act":
      b.vx = b.vz = 0;
      if (br.act === "bus") {
        if (sim.bus.state === "docked") { walkTo(sim, b, M.BUS.door.x, M.BUS.door.z, "board", -1); return; }
      }
      if (now >= br.until) { const wasLace = br.act === "shoelace"; npcPlan(sim, b, !wasLace); return; }
      if ((br.act === "look" || br.act === "rim" || br.act === "drain" || br.act === "bus") && now >= br.lookT) {
        br.lookT = now + range(sim.rng, 1.5, 3.5);
        sim.setAnim(b, b.anim === "look_around" ? "idle" : "look_around");
      }
      return;
    case "wait": {
      b.vx = b.vz = 0;
      const q = br.partner;
      if (!q || !q.visible || q.brain?.partner !== b || now >= br.waitUntil) { npcPlan(sim, b); return; }
      if (q.brain.task === "wait" && q.mode === "free") {
        const until = now + range(sim.rng, T.CHAT_MIN, T.CHAT_MAX);
        startAct(sim, b, "chat", until); startAct(sim, q, "chat", until);
      }
      return;
    }
    case "board":
      b.vx = b.vz = 0;
      if (sim.bus.state !== "docked") { startAct(sim, b, "bus", Infinity); }
      return;
    case "conga": conga(sim, b); return;
  }
}

function follow(sim: Sim, b: Body, now: number) {
  const br = b.brain!;
  let wp = br.path[br.pi];
  while (wp && Math.hypot(wp.x - b.x, wp.z - b.z) < T.ARRIVE_R) { br.pi++; wp = br.path[br.pi]; }
  if (!wp) { arrive(sim, b); return; }
  const dx = wp.x - b.x, dz = wp.z - b.z, d = Math.hypot(dx, dz);
  let vx = (dx / d) * T.WALK, vz = (dz / d) * T.WALK;
  // separation: ease away from close neighbours (never stops the walk; the sim's push resolves contact)
  for (const q of sim.bodies) {
    if (q === b || !q.visible) continue;
    const ox = b.x - q.x, oz = b.z - q.z; if (Math.abs(ox) > T.SEPARATION_R || Math.abs(oz) > T.SEPARATION_R) continue;
    const od = Math.hypot(ox, oz); if (od >= T.SEPARATION_R || od < 1e-6) continue;
    const k = ((T.SEPARATION_R - od) / T.SEPARATION_R) * T.SEPARATION_K;
    vx += (ox / od) * k; vz += (oz / od) * k;
  }
  const sp = Math.hypot(vx, vz); if (sp > T.WALK) { vx = (vx / sp) * T.WALK; vz = (vz / sp) * T.WALK; }
  b.vx = vx; b.vz = vz; b.face = null;
  if (b.anim !== "walk") sim.setAnim(b, "walk");
  // no progress for a while (a crowd, the float in the way): find a new way, then give up on the goal
  if (now - br.stuckAt >= T.STUCK_SEC) {
    const moved = Math.hypot(b.x - br.stuckX, b.z - br.stuckZ);
    br.stuckAt = now; br.stuckX = b.x; br.stuckZ = b.z;
    if (moved < 0.4) {
      if (++br.replans > 2) { npcPlan(sim, b, false); return; }
      br.path = M.findPath(b.x, b.z, br.goal.x, br.goal.z) || []; br.pi = 0;
    }
  }
}

function flee(sim: Sim, b: Body) {
  const br = b.brain!;
  let ax = br.fleeX, az = br.fleeZ;
  // steer round obstacles: try the flee direction, then fan out either side
  const look = 0.8;
  for (const turn of [0, 0.5, -0.5, 1.0, -1.0, 1.6, -1.6, 2.4, -2.4]) {
    const c = Math.cos(turn), s = Math.sin(turn), dx = br.fleeX * c - br.fleeZ * s, dz = br.fleeX * s + br.fleeZ * c;
    if (!M.blockedAt(b.x + dx * look, b.z + dz * look)) { ax = dx; az = dz; break; }
  }
  b.vx = ax * T.PANIC_RUN; b.vz = az * T.PANIC_RUN; b.face = null;
  if (b.anim !== "run") sim.setAnim(b, "run");
}

function conga(sim: Sim, b: Body) {
  const br = b.brain!;
  if (!sim.float) { br.task = "idle"; br.slot = -1; npcPlan(sim, b); return; }
  const p = M.floatAt(sim.floatS - (T.FLOAT_R + 0.8 + br.slot * T.CONGA_GAP));
  const dx = p.x - b.x, dz = p.z - b.z, d = Math.hypot(dx, dz);
  const sp = Math.min(T.WALK, d * 4);
  b.vx = d > 1e-3 ? (dx / d) * sp : 0; b.vz = d > 1e-3 ? (dz / d) * sp : 0; b.face = p.rot;
  sim.setAnim(b, d > 0.7 ? "walk" : "dance");
}
